/**
 * Webtech HR Engine™ - HR Skills Intelligence Module
 * Page principale de gestion des compétences
 * Structure hiérarchique: Catégorie → Sous-catégorie → Compétence
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Brain,
  Plus,
  Search,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  MoreVertical,
  Pencil,
  Trash2,
  Download,
  Upload,
  Sparkles,
  BarChart3,
  Users,
  Target,
  Briefcase,
  BookOpen,
  Loader2,
  Check,
  X,
  FolderPlus,
  FilePlus,
  Layers,
  Award
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

// Icônes disponibles pour les catégories
const CATEGORY_ICONS = [
  { value: 'code', label: 'Code' },
  { value: 'users', label: 'Utilisateurs' },
  { value: 'crown', label: 'Leadership' },
  { value: 'briefcase', label: 'Business' },
  { value: 'laptop', label: 'Digital' },
  { value: 'globe', label: 'Langues' },
  { value: 'brain', label: 'Analyse' },
  { value: 'heart', label: 'Social' },
  { value: 'shield', label: 'Sécurité' },
  { value: 'book', label: 'Formation' },
];

// Couleurs disponibles
const CATEGORY_COLORS = [
  { value: '#2054A6', label: 'Bleu Webtech' },
  { value: '#22C55E', label: 'Vert' },
  { value: '#F59E0B', label: 'Orange' },
  { value: '#8B5CF6', label: 'Violet' },
  { value: '#EC4899', label: 'Rose' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#EF4444', label: 'Rouge' },
  { value: '#64748B', label: 'Gris' },
];

export default function SkillsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId;
  
  // State
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('library');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  
  // Dialog states
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'create', data: null });
  const [subcategoryDialog, setSubcategoryDialog] = useState({ open: false, mode: 'create', data: null, parentCategoryId: null });
  const [skillDialog, setSkillDialog] = useState({ open: false, mode: 'create', data: null, parentSubcategoryId: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
  const [seeding, setSeeding] = useState(false);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', icon: 'folder', color: '#2054A6', description: '' });
  const [subcategoryForm, setSubcategoryForm] = useState({ name: '', categoryId: '', icon: 'folder', description: '' });
  const [skillForm, setSkillForm] = useState({ name: '', subcategoryId: '', description: '' });

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, skillsRes, statsRes] = await Promise.all([
        api.get(`/skills/categories?company_id=${companyId}`),
        api.get(`/skills/library?company_id=${companyId}`),
        api.get(`/skills/stats?company_id=${companyId}`)
      ]);
      
      setCategories(categoriesRes.data.categories || []);
      setSkills(skillsRes.data.skills || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      setSeeding(true);
      const response = await api.post(`/skills/seed-defaults?company_id=${companyId}`);
      if (response.data.seeded) {
        toast.success(`Données par défaut créées: ${response.data.categoriesCreated} catégories, ${response.data.subcategoriesCreated} sous-catégories, ${response.data.skillsCreated} compétences`);
        loadData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la création des données par défaut');
    } finally {
      setSeeding(false);
    }
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Toggle subcategory expansion
  const toggleSubcategory = (subcategoryId) => {
    setExpandedSubcategories(prev => ({
      ...prev,
      [subcategoryId]: !prev[subcategoryId]
    }));
  };

  // === Category CRUD ===
  const openCategoryDialog = (mode, data = null) => {
    if (mode === 'edit' && data) {
      setCategoryForm({
        name: data.name,
        icon: data.icon || 'folder',
        color: data.color || '#2054A6',
        description: data.description || ''
      });
    } else {
      setCategoryForm({ name: '', icon: 'folder', color: '#2054A6', description: '' });
    }
    setCategoryDialog({ open: true, mode, data });
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    
    try {
      if (categoryDialog.mode === 'create') {
        await api.post(`/skills/categories?company_id=${companyId}`, categoryForm);
        toast.success('Catégorie créée');
      } else {
        await api.put(`/skills/categories/${categoryDialog.data.id}?company_id=${companyId}`, categoryForm);
        toast.success('Catégorie mise à jour');
      }
      setCategoryDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  // === Subcategory CRUD ===
  const openSubcategoryDialog = (mode, data = null, parentCategoryId = null) => {
    if (mode === 'edit' && data) {
      setSubcategoryForm({
        name: data.name,
        categoryId: data.categoryId,
        icon: data.icon || 'folder',
        description: data.description || ''
      });
    } else {
      setSubcategoryForm({
        name: '',
        categoryId: parentCategoryId || '',
        icon: 'folder',
        description: ''
      });
    }
    setSubcategoryDialog({ open: true, mode, data, parentCategoryId });
  };

  const handleSaveSubcategory = async () => {
    if (!subcategoryForm.name.trim() || !subcategoryForm.categoryId) {
      toast.error('Le nom et la catégorie sont requis');
      return;
    }
    
    try {
      if (subcategoryDialog.mode === 'create') {
        await api.post(`/skills/subcategories?company_id=${companyId}`, subcategoryForm);
        toast.success('Sous-catégorie créée');
      } else {
        await api.put(`/skills/subcategories/${subcategoryDialog.data.id}?company_id=${companyId}`, subcategoryForm);
        toast.success('Sous-catégorie mise à jour');
      }
      setSubcategoryDialog({ open: false, mode: 'create', data: null, parentCategoryId: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  // === Skill CRUD ===
  const openSkillDialog = (mode, data = null, parentSubcategoryId = null) => {
    if (mode === 'edit' && data) {
      setSkillForm({
        name: data.name,
        subcategoryId: data.subcategoryId,
        description: data.description || ''
      });
    } else {
      setSkillForm({
        name: '',
        subcategoryId: parentSubcategoryId || '',
        description: ''
      });
    }
    setSkillDialog({ open: true, mode, data, parentSubcategoryId });
  };

  const handleSaveSkill = async () => {
    if (!skillForm.name.trim() || !skillForm.subcategoryId) {
      toast.error('Le nom et la sous-catégorie sont requis');
      return;
    }
    
    try {
      if (skillDialog.mode === 'create') {
        await api.post(`/skills/library?company_id=${companyId}`, skillForm);
        toast.success('Compétence créée');
      } else {
        await api.put(`/skills/library/${skillDialog.data.id}?company_id=${companyId}`, skillForm);
        toast.success('Compétence mise à jour');
      }
      setSkillDialog({ open: false, mode: 'create', data: null, parentSubcategoryId: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  // === Delete ===
  const handleDelete = async () => {
    try {
      const { type, item } = deleteDialog;
      let endpoint = '';
      
      switch (type) {
        case 'category':
          endpoint = `/skills/categories/${item.id}?company_id=${companyId}`;
          break;
        case 'subcategory':
          endpoint = `/skills/subcategories/${item.id}?company_id=${companyId}`;
          break;
        case 'skill':
          endpoint = `/skills/library/${item.id}?company_id=${companyId}`;
          break;
        default:
          return;
      }
      
      await api.delete(endpoint);
      toast.success('Élément supprimé');
      setDeleteDialog({ open: false, type: null, item: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Get all subcategories as flat list
  const getAllSubcategories = () => {
    return categories.flatMap(cat => cat.subcategories || []);
  };

  // Filter skills based on search
  const filteredSkills = skills.filter(skill => 
    skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    skill.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if user is admin
  const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';

  return (
    <DashboardLayout title="Compétences">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Brain className="h-7 w-7 text-webtech-blue" />
              HR Skills Intelligence™
            </h1>
            <p className="text-slate-500 mt-1">Gérez le référentiel de compétences de votre entreprise</p>
          </div>
          
          {isAdmin && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => navigate('/skills/dashboard')}>
                <BarChart3 className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate('/skills/campaigns')}>
                <Target className="h-4 w-4 mr-2" />
                Campagnes
              </Button>
              <Button variant="outline" onClick={() => navigate('/skills/profiles')}>
                <Briefcase className="h-4 w-4 mr-2" />
                Profils
              </Button>
              <Button variant="outline" onClick={() => navigate('/skills/development-plans')}>
                <BookOpen className="h-4 w-4 mr-2" />
                Plans
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openCategoryDialog('create')}>
                    <Folder className="h-4 w-4 mr-2" />
                    Nouvelle catégorie
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openSubcategoryDialog('create')}>
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Nouvelle sous-catégorie
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openSkillDialog('create')}>
                    <FilePlus className="h-4 w-4 mr-2" />
                    Nouvelle compétence
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Catégories</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.categories || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Folder className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Sous-catégories</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.subcategories || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Layers className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Compétences</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.skills || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Award className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Profils de poste</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.jobProfiles || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Empty State or Library */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
          </div>
        ) : categories.length === 0 ? (
          <Card className="py-12">
            <CardContent className="text-center">
              <Brain className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune compétence définie</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                Commencez par créer des catégories et compétences, ou importez notre bibliothèque par défaut.
              </p>
              {isAdmin && (
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => openCategoryDialog('create')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Créer manuellement
                  </Button>
                  <Button onClick={handleSeedDefaults} disabled={seeding}>
                    {seeding ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    Importer la bibliothèque par défaut
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="library">Bibliothèque</TabsTrigger>
              <TabsTrigger value="list">Liste des compétences</TabsTrigger>
            </TabsList>
            
            <TabsContent value="library" className="mt-4">
              {/* Hierarchical Tree View */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Structure du référentiel</CardTitle>
                  <CardDescription>
                    Catégories → Sous-catégories → Compétences
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {categories.map(category => (
                      <div key={category.id} className="border rounded-lg">
                        {/* Category Row */}
                        <div 
                          className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleCategory(category.id)}
                        >
                          <div className="flex items-center gap-3">
                            {expandedCategories[category.id] ? (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            )}
                            <div 
                              className="h-8 w-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${category.color}20` }}
                            >
                              <Folder className="h-4 w-4" style={{ color: category.color }} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{category.name}</p>
                              <p className="text-xs text-slate-500">
                                {category.subcategoriesCount || 0} sous-catégories · {category.skillsCount || 0} compétences
                              </p>
                            </div>
                          </div>
                          
                          {isAdmin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  openSubcategoryDialog('create', null, category.id);
                                }}>
                                  <FolderPlus className="h-4 w-4 mr-2" />
                                  Ajouter sous-catégorie
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  openCategoryDialog('edit', category);
                                }}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Modifier
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteDialog({ open: true, type: 'category', item: category });
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                        
                        {/* Subcategories */}
                        {expandedCategories[category.id] && category.subcategories?.length > 0 && (
                          <div className="border-t bg-slate-50/50">
                            {category.subcategories.map(subcat => (
                              <div key={subcat.id}>
                                {/* Subcategory Row */}
                                <div 
                                  className="flex items-center justify-between p-3 pl-10 hover:bg-slate-100 cursor-pointer"
                                  onClick={() => toggleSubcategory(subcat.id)}
                                >
                                  <div className="flex items-center gap-3">
                                    {expandedSubcategories[subcat.id] ? (
                                      <ChevronDown className="h-4 w-4 text-slate-400" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-slate-400" />
                                    )}
                                    <FolderOpen className="h-4 w-4 text-slate-500" />
                                    <div>
                                      <p className="font-medium text-slate-700">{subcat.name}</p>
                                      <p className="text-xs text-slate-500">{subcat.skillsCount || 0} compétences</p>
                                    </div>
                                  </div>
                                  
                                  {isAdmin && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                                        <Button variant="ghost" size="icon">
                                          <MoreVertical className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={(e) => {
                                          e.stopPropagation();
                                          openSkillDialog('create', null, subcat.id);
                                        }}>
                                          <FilePlus className="h-4 w-4 mr-2" />
                                          Ajouter compétence
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={(e) => {
                                          e.stopPropagation();
                                          openSubcategoryDialog('edit', subcat);
                                        }}>
                                          <Pencil className="h-4 w-4 mr-2" />
                                          Modifier
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                          className="text-red-600"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteDialog({ open: true, type: 'subcategory', item: subcat });
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Supprimer
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                                
                                {/* Skills */}
                                {expandedSubcategories[subcat.id] && (
                                  <div className="bg-white border-t">
                                    {skills
                                      .filter(s => s.subcategoryId === subcat.id)
                                      .map(skill => (
                                        <div 
                                          key={skill.id}
                                          className="flex items-center justify-between p-3 pl-20 hover:bg-slate-50 border-b last:border-b-0"
                                        >
                                          <div className="flex items-center gap-3">
                                            <Award className="h-4 w-4 text-green-500" />
                                            <div>
                                              <p className="text-sm font-medium text-slate-700">{skill.name}</p>
                                              {skill.description && (
                                                <p className="text-xs text-slate-500">{skill.description}</p>
                                              )}
                                            </div>
                                          </div>
                                          
                                          {isAdmin && (
                                            <DropdownMenu>
                                              <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon">
                                                  <MoreVertical className="h-4 w-4" />
                                                </Button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => openSkillDialog('edit', skill)}>
                                                  <Pencil className="h-4 w-4 mr-2" />
                                                  Modifier
                                                </DropdownMenuItem>
                                                <DropdownMenuItem 
                                                  className="text-red-600"
                                                  onClick={() => setDeleteDialog({ open: true, type: 'skill', item: skill })}
                                                >
                                                  <Trash2 className="h-4 w-4 mr-2" />
                                                  Supprimer
                                                </DropdownMenuItem>
                                              </DropdownMenuContent>
                                            </DropdownMenu>
                                          )}
                                        </div>
                                      ))}
                                    {skills.filter(s => s.subcategoryId === subcat.id).length === 0 && (
                                      <div className="p-3 pl-20 text-sm text-slate-400 italic">
                                        Aucune compétence
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {expandedCategories[category.id] && (!category.subcategories || category.subcategories.length === 0) && (
                          <div className="p-3 pl-10 text-sm text-slate-400 italic border-t bg-slate-50/50">
                            Aucune sous-catégorie
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="list" className="mt-4">
              {/* Flat List View with Search */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Toutes les compétences</CardTitle>
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="divide-y">
                    {filteredSkills.map(skill => (
                      <div key={skill.id} className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="h-8 w-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${skill.category?.color || '#64748B'}20` }}
                          >
                            <Award className="h-4 w-4" style={{ color: skill.category?.color || '#64748B' }} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{skill.name}</p>
                            <p className="text-xs text-slate-500">
                              {skill.category?.name} → {skill.subcategory?.name}
                            </p>
                          </div>
                        </div>
                        {skill.description && (
                          <p className="text-sm text-slate-500 max-w-md truncate hidden lg:block">
                            {skill.description}
                          </p>
                        )}
                      </div>
                    ))}
                    {filteredSkills.length === 0 && (
                      <div className="py-8 text-center text-slate-500">
                        Aucune compétence trouvée
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialog.open} onOpenChange={(open) => setCategoryDialog({ ...categoryDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {categoryDialog.mode === 'create' ? 'Nouvelle catégorie' : 'Modifier la catégorie'}
            </DialogTitle>
            <DialogDescription>
              Les catégories regroupent les sous-catégories de compétences.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Ex: Compétences Techniques"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Couleur</Label>
                <Select value={categoryForm.color} onValueChange={(v) => setCategoryForm({ ...categoryForm, color: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_COLORS.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded" style={{ backgroundColor: c.value }} />
                          {c.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Icône</Label>
                <Select value={categoryForm.icon} onValueChange={(v) => setCategoryForm({ ...categoryForm, icon: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_ICONS.map(i => (
                      <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Description optionnelle..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialog({ ...categoryDialog, open: false })}>
              Annuler
            </Button>
            <Button onClick={handleSaveCategory}>
              {categoryDialog.mode === 'create' ? 'Créer' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={subcategoryDialog.open} onOpenChange={(open) => setSubcategoryDialog({ ...subcategoryDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {subcategoryDialog.mode === 'create' ? 'Nouvelle sous-catégorie' : 'Modifier la sous-catégorie'}
            </DialogTitle>
            <DialogDescription>
              Les sous-catégories regroupent les compétences individuelles.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Catégorie parente *</Label>
              <Select 
                value={subcategoryForm.categoryId} 
                onValueChange={(v) => setSubcategoryForm({ ...subcategoryForm, categoryId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Nom *</Label>
              <Input
                value={subcategoryForm.name}
                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
                placeholder="Ex: Développement"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={subcategoryForm.description}
                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
                placeholder="Description optionnelle..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubcategoryDialog({ ...subcategoryDialog, open: false })}>
              Annuler
            </Button>
            <Button onClick={handleSaveSubcategory}>
              {subcategoryDialog.mode === 'create' ? 'Créer' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Skill Dialog */}
      <Dialog open={skillDialog.open} onOpenChange={(open) => setSkillDialog({ ...skillDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {skillDialog.mode === 'create' ? 'Nouvelle compétence' : 'Modifier la compétence'}
            </DialogTitle>
            <DialogDescription>
              Ajoutez une compétence au référentiel de votre entreprise.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Sous-catégorie *</Label>
              <Select 
                value={skillForm.subcategoryId} 
                onValueChange={(v) => setSkillForm({ ...skillForm, subcategoryId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une sous-catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <div key={cat.id}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                        {cat.name}
                      </div>
                      {cat.subcategories?.map(subcat => (
                        <SelectItem key={subcat.id} value={subcat.id}>
                          {subcat.name}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Nom *</Label>
              <Input
                value={skillForm.name}
                onChange={(e) => setSkillForm({ ...skillForm, name: e.target.value })}
                placeholder="Ex: Python"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={skillForm.description}
                onChange={(e) => setSkillForm({ ...skillForm, description: e.target.value })}
                placeholder="Description de la compétence..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkillDialog({ ...skillDialog, open: false })}>
              Annuler
            </Button>
            <Button onClick={handleSaveSkill}>
              {skillDialog.mode === 'create' ? 'Créer' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer {deleteDialog.type === 'category' ? 'cette catégorie' : deleteDialog.type === 'subcategory' ? 'cette sous-catégorie' : 'cette compétence'} 
              <strong> "{deleteDialog.item?.name}"</strong> ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
